<template>
    <div>
        <card-header title="New Ticket" icon="fa-plus"/>

        <card-body>
            <card-list>
                <subheader-list-item icon="fa-satellite-dish" title="Channel"/>

                <container-list-item padded no-border>
                    <div class="columns">
                        <div class="column">
                            <b-field label="Domain" label-position="inside">
                                <b-select v-model="form.domain_id" expanded @input="onDomainChange">
                                    <option :value="null">Select domain...</option>
                                    <option v-for="d in domains" :key="d.id" :value="d.id">{{d.name}}</option>
                                </b-select>
                            </b-field>
                        </div>
                        <div class="column">
                            <b-field label="Channel" label-position="inside">
                                <b-select v-model="form.channel_id" expanded @input="onChannelChange">
                                    <option :value="null">Select channel...</option>
                                    <option v-for="ch in filteredChannels" :key="ch.id" :value="ch.id">{{ch.name}} ({{ch.channel_type}})</option>
                                </b-select>
                            </b-field>
                        </div>
                    </div>
                </container-list-item>

                <subheader-list-item icon="fa-user" title="Recipient"/>

                <container-list-item padded no-border>
                    <div class="columns">
                        <div class="column">
                            <b-field :label="selectedChannelType === 'sms' ? 'Search by name, email, or phone' : 'Search by name or email'" label-position="inside">
                                <b-input v-model="userSearch" @input="searchUsers" icon="search"></b-input>
                            </b-field>
                            <div class="user-results" v-if="userResults.length">
                                <div v-for="u in userResults" :key="u.id" class="user-result" @click="selectUser(u)">
                                    <strong>{{u.first_name}} {{u.last_name}}</strong> - {{u.email}}
                                </div>
                            </div>
                            <div class="selected-user" v-if="selectedUser">
                                <i class="fas fa-user mr-1"></i>{{selectedUser.first_name}} {{selectedUser.last_name}} ({{selectedUser.email}})
                                <button class="delete is-small ml-2" @click="clearUser"></button>
                            </div>
                        </div>
                    </div>
                    <div class="columns" v-if="selectedChannelType === 'sms' && selectedUser && userPhones.length">
                        <div class="column">
                            <div class="phone-options">
                                <b-radio v-for="phone in userPhones" :key="phone" v-model="selectedPhone" :native-value="phone">
                                    {{phone}}
                                </b-radio>
                            </div>
                        </div>
                    </div>
                    <div class="columns" v-if="selectedChannelType === 'sms' && !selectedUser">
                        <div class="column">
                            <b-field label="Or enter phone number directly" label-position="inside">
                                <b-input v-model="form.phone_from" type="tel"></b-input>
                            </b-field>
                        </div>
                    </div>
                    <div class="columns" v-if="selectedChannelType === 'sms' && selectedUser">
                        <div class="column">
                            <b-field label="Or enter phone number directly" label-position="inside">
                                <b-input v-model="form.phone_from" type="tel"></b-input>
                            </b-field>
                        </div>
                    </div>
                    <div class="columns" v-if="!selectedUser && selectedChannelType === 'email'">
                        <div class="column">
                            <b-field label="Or enter email directly" label-position="inside">
                                <b-input v-model="form.email_from" type="email"></b-input>
                            </b-field>
                        </div>
                    </div>
                </container-list-item>

                <subheader-list-item icon="fa-align-left" title="Ticket Details"/>

                <container-list-item padded no-border>
                    <div class="columns">
                        <div class="column">
                            <b-field label="Priority" label-position="inside">
                                <b-select v-model="form.priority" expanded>
                                    <option value="low">Low</option>
                                    <option value="normal">Normal</option>
                                    <option value="high">High</option>
                                    <option value="urgent">Urgent</option>
                                </b-select>
                            </b-field>
                        </div>
                    </div>
                    <div class="columns">
                        <div class="column">
                            <b-field label-position="inside">
                                <template #label>Subject <span class="required-flag">*</span></template>
                                <b-input v-model="form.subject"></b-input>
                            </b-field>
                        </div>
                    </div>
                    <div class="columns">
                        <div class="column">
                            <div v-if="selectedChannelType === 'sms'">
                                <b-field label-position="inside">
                                    <template #label>Message <span class="required-flag">*</span></template>
                                    <b-input type="textarea" v-model="form.body_text" rows="4"></b-input>
                                </b-field>
                                <div class="sms-char-count has-text-grey is-size-7">{{(form.body_text || '').length}} / 160 characters</div>
                            </div>
                            <div v-else>
                                <label class="label is-size-7">Message <span class="required-flag">*</span></label>
                                <quill-editor v-model="form.body_html" :options="editorOptions" class="has-background-white content"></quill-editor>
                            </div>
                        </div>
                    </div>
                    <div class="columns" v-if="selectedChannelType === 'email'">
                        <div class="column">
                            <b-switch v-model="form.send_email">Send email to recipient</b-switch>
                        </div>
                    </div>
                </container-list-item>
            </card-list>
        </card-body>

        <card-footer class="pt-3 pb-3">
            <div class="columns">
                <div class="column has-text-right">
                    <b-button
                        type="is-primary"
                        size="is-small"
                        @click="submit"
                        :disabled="!form.channel_id || !form.subject || !hasMessage"
                    ><i class="fas fa-plus mr-2"></i>New Ticket
                    </b-button>
                </div>
            </div>
        </card-footer>
    </div>
</template>

<script>
    import CardHeader from "@/TIER/components/CardHeader";
    import CardBody from "@/TIER/components/CardBody";
    import CardList from "@/TIER/components/CardList";
    import CardFooter from "@/components/common/CardFooter";
    import SubheaderListItem from "@/TIER/components/SubheaderListItem";
    import ContainerListItem from "@/TIER/components/ContainerListItem";
    import {client as http} from '@/http_client';

    let searchTimeout = null;

    export default {
        components: {ContainerListItem, SubheaderListItem, CardList, CardFooter, CardBody, CardHeader},
        props: ['card', 'props'],
        data() {
            return {
                form: {
                    domain_id: null,
                    channel_id: null,
                    subject: '',
                    body_text: '',
                    body_html: '',
                    priority: 'normal',
                    email_from: '',
                    phone_from: '',
                    send_email: true
                },
                domains: [],
                channels: [],
                editorOptions: {
                    modules: {
                        toolbar: [
                            ['bold', 'italic', 'underline'],
                            [{'color': []}, {'background': []}],
                            [{'list': 'ordered'}, {'list': 'bullet'}],
                            ['link'],
                            ['clean']
                        ],
                        keyboard: {
                            bindings: {
                                linebreak: {
                                    key: 13,
                                    shiftKey: true,
                                    handler: function(range) {
                                        this.quill.insertEmbed(range.index, 'break', true, 'user');
                                        this.quill.setSelection(range.index + 1, 'silent');
                                        return false;
                                    }
                                }
                            }
                        }
                    },
                    placeholder: 'Compose message...'
                },
                userSearch: '',
                userResults: [],
                selectedUser: null,
                selectedPhone: null
            }
        },
        computed: {
            filteredChannels() {
                if (!this.form.domain_id) return this.channels;
                return this.channels.filter(c => c.domain_id === this.form.domain_id);
            },
            selectedChannelType() {
                if (!this.form.channel_id) return null;
                let ch = this.channels.find(c => c.id === this.form.channel_id);
                return ch ? ch.channel_type : null;
            },
            userPhones() {
                if (!this.selectedUser) return [];
                return [this.selectedUser.phone, this.selectedUser.phone_2].filter(Boolean);
            },
            hasMessage() {
                if (this.selectedChannelType === 'sms') return (this.form.body_text || '').trim().length > 0;
                return (this.form.body_html || '').replace(/<[^>]*>/g, '').trim().length > 0;
            }
        },
        methods: {
            searchUsers() {
                if (searchTimeout) clearTimeout(searchTimeout);
                if (!this.userSearch || this.userSearch.length < 2) {
                    this.userResults = [];
                    return;
                }
                searchTimeout = setTimeout(() => {
                    http.get('users/search/' + encodeURIComponent(this.userSearch) + '/0/10').then(response => {
                        this.userResults = response.data.users || response.data || [];
                    }).catch(() => {
                        this.userResults = [];
                    });
                }, 300);
            },
            selectUser(user) {
                this.selectedUser = user;
                this.userSearch = '';
                this.userResults = [];
                this.form.email_from = user.email;
                if (this.selectedChannelType === 'sms') {
                    let phones = [user.phone, user.phone_2].filter(Boolean);
                    this.selectedPhone = phones.length ? phones[0] : null;
                }
            },
            clearUser() {
                this.selectedUser = null;
                this.selectedPhone = null;
            },
            loadDomains() {
                http.get('support/domains').then(response => {
                    this.domains = response.data || [];
                    if (this.domains.length === 1) {
                        this.form.domain_id = this.domains[0].id;
                    }
                }).catch(() => {});
            },
            loadChannels() {
                http.get('support/channels').then(response => {
                    this.channels = response.data || [];
                    if (this.filteredChannels.length === 1) {
                        this.form.channel_id = this.filteredChannels[0].id;
                    }
                }).catch(() => {});
            },
            onDomainChange() {
                this.form.channel_id = null;
                // Auto-select if only one channel in domain
                if (this.filteredChannels.length === 1) {
                    this.form.channel_id = this.filteredChannels[0].id;
                }
            },
            onChannelChange() {
                this.selectedUser = null;
                this.selectedPhone = null;
                this.form.phone_from = '';
                this.form.email_from = '';
                this.userSearch = '';
                this.userResults = [];
            },
            submit() {
                let hasMessage = this.selectedChannelType === 'sms'
                    ? (this.form.body_text || '').trim()
                    : (this.form.body_html || '').replace(/<[^>]*>/g, '').trim();
                if (!this.form.subject.trim() || !hasMessage) return;

                this.$emit('loading', true);
                let payload = {
                    ...this.form,
                    body_html: this.selectedChannelType === 'sms' ? this.form.body_text.replace(/\n/g, '<br>') : this.form.body_html,
                    body_text: this.selectedChannelType === 'sms' ? this.form.body_text : (this.form.body_html || '').replace(/<[^>]*>/g, ''),
                    user_id: this.selectedUser ? this.selectedUser.id : null,
                    email_from: this.selectedUser ? this.selectedUser.email : this.form.email_from,
                    phone_from: this.selectedChannelType === 'sms' ? (this.selectedPhone || this.form.phone_from) : this.form.phone_from
                };

                http.post('support/tickets', payload).then(response => {
                    this.$emit('loading', false);
                    this.$openCard('support-ticket', {ticketId: response.data.id}, this.card);
                }).catch(err => {
                    window.alert(err.response?.data?.message || 'An error occurred.');
                    this.$emit('loading', false);
                });
            }
        },
        mounted() {
            this.loadDomains();
            this.loadChannels();
        }
    }
</script>

<style lang="scss" scoped>
    .user-results {
        border: 1px solid #e0e0e0;
        border-radius: 4px;
        max-height: 150px;
        overflow-y: auto;
        margin-top: 4px;
    }
    .user-result {
        padding: 6px 10px;
        font-size: 0.85rem;
        cursor: pointer;
        &:hover {
            background: #f5f5f5;
        }
    }
    .selected-user {
        display: inline-flex;
        align-items: center;
        background: #e8f4fd;
        padding: 4px 10px;
        border-radius: 4px;
        font-size: 0.85rem;
        margin-top: 6px;
    }
    .phone-options {
        display: flex;
        flex-direction: column;
        gap: 6px;
        margin-bottom: 8px;
    }
    .sms-char-count {
        margin-top: 4px;
    }
</style>
